import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RichTextEditor from "./editor/RichTextEditor";
import TemplateLibrary from "./TemplateLibrary";
import { buildEmailHTML, nonEmpty } from "./utils/emailHtml";
import { emailCampaignsApi } from "./api/emailCampaignsApi";
import { companySignatureApi } from "./api/companySignatureApi";
import { normalizePositiveInteger } from "./audienceUtils";
import { intelligenceApi } from "../../api/intelligenceApi";

const DEFAULT_REPLY_EMAIL = "abisiniyatravels@gmail.com";

function resolveReplyEmail(mode, customEmail, myEmail) {
  if (mode === "my_email") return myEmail || DEFAULT_REPLY_EMAIL;
  if (mode === "other")    return (customEmail || "").trim() || DEFAULT_REPLY_EMAIL;
  return DEFAULT_REPLY_EMAIL;
}

const MERGE_TAGS = [
  { label: "First name",   tag: "{{first_name}}"  },
  { label: "Full name",    tag: "{{full_name}}"   },
  { label: "Company",      tag: "{{company}}"     },
  { label: "Destination",  tag: "{{destination}}" },
  { label: "Greeting",     tag: "{{greeting}}"    },
];

const SAMPLE_CONTACTS = [
  { name: "Sarah Johnson", email: "sarah@example.com", destination: "Cape Town",      company: "Safari Co" },
  { name: "David Moyo",    email: "david@example.com", destination: "Victoria Falls", company: ""           },
  { name: "Emily Chen",    email: "emily@example.com", destination: "Zanzibar",       company: "Globe Tours" },
];

function applyMergeTagsLocally(html, contact) {
  const firstName = contact.name.split(" ")[0] || "there";
  return html
    .replace(/{{first_name}}/g,  firstName)
    .replace(/{{full_name}}/g,   contact.name)
    .replace(/{{company}}/g,     contact.company || "")
    .replace(/{{destination}}/g, contact.destination)
    .replace(/{{greeting}}/g,    `Hi ${firstName}`);
}

export default function Composer({
  audienceById,
  selectedIds,
  audiencePlan,
  audienceSummary,
  previewRows,
  subject,
  setSubject,
  bodyHtml,
  setBodyHtml,
  afterSendStatus,
  setAfterSendStatus,
  replyToMode,
  setReplyToMode,
  replyToCustomEmail,
  setReplyToCustomEmail,
  signature,
  excludeRecent,
  recentDays,
  onQueued,
  hidePrimaryAction = false,
  onMetaChange,
  onLaunch,
  personalisationMode,
  setPersonalisationMode,
  pacingMode = "immediate",
  pacingDays = 0,
}) {
  const [isSending, setIsSending]           = useState(false);
  const [sendError, setSendError]           = useState("");
  const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const [spamWarnings, setSpamWarnings]     = useState([]);
  const [spamChecking, setSpamChecking]     = useState(false);
  const [aiDraftBrief, setAiDraftBrief]     = useState("");
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiDraftError, setAiDraftError]     = useState("");
  const [companySignatureExists, setCompanySignatureExists] = useState(false);
  const spamTimerRef   = useRef(null);
  const launchHandlerRef = useRef(null);

  // Does an admin-configured company signature exist? Used to tell senders
  // without a personal signature what their email will fall back to.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await companySignatureApi.get();
        if (!cancelled) setCompanySignatureExists(Boolean(data?.exists));
      } catch {
        if (!cancelled) setCompanySignatureExists(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Preview state
  const [sampleIndex, setSampleIndex]       = useState(0);
  const [aiPreviews, setAiPreviews]         = useState([]);
  const [aiPreviewTab, setAiPreviewTab]     = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewDebounceRef = useRef(null);

  const picked = useMemo(
    () => selectedIds.map((id) => audienceById.get(String(id))).filter(Boolean),
    [selectedIds, audienceById]
  );
  const preferredLimit = useMemo(
    () => normalizePositiveInteger(audiencePlan?.filters?.preferred_limit),
    [audiencePlan]
  );
  const effectiveAudienceCount = useMemo(() => {
    if (audiencePlan?.deliveryMode === "filter_all") {
      return Number(audienceSummary?.eligibleFound || 0);
    }
    return preferredLimit ? Math.min(selectedIds.length, preferredLimit) : selectedIds.length;
  }, [audiencePlan, audienceSummary, preferredLimit, selectedIds.length]);

  const canSend = useMemo(
    () => effectiveAudienceCount > 0 && nonEmpty(subject) && nonEmpty(bodyHtml) && !isSending,
    [effectiveAudienceCount, subject, bodyHtml, isSending]
  );
  const canGenerateAiDraft = nonEmpty(aiDraftBrief) && !aiDraftLoading;

  useEffect(() => { onMetaChange?.({ canSend, isSending }); }, [canSend, isSending, onMetaChange]);

  // Spam check
  useEffect(() => {
    clearTimeout(spamTimerRef.current);
    if (!nonEmpty(subject) || !nonEmpty(bodyHtml)) { setSpamWarnings([]); return; }
    setSpamChecking(true);
    spamTimerRef.current = setTimeout(async () => {
      try {
        const data = await emailCampaignsApi.checkSpamSafety({ subject, body_html: bodyHtml, emails: [], days: 7 });
        const warnings = [];
        if (Array.isArray(data?.warnings) && data.warnings.length > 0) warnings.push(...data.warnings);
        if (Number(data?.excludedCount || 0) > 0) warnings.push(`${data.excludedCount} recipients may be excluded due to spam filtering.`);
        setSpamWarnings(warnings);
      } catch { /* non-blocking */ } finally { setSpamChecking(false); }
    }, 1500);
    return () => clearTimeout(spamTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, bodyHtml]);

  // AI preview debounce
  const fetchAiPreviews = useCallback(() => {
    if (!nonEmpty(subject) || !nonEmpty(bodyHtml) || personalisationMode !== "ai_generated") return;
    setPreviewLoading(true);
    intelligenceApi.previewPersonalised({ subject, body_html: bodyHtml, personalisation_mode: "ai_generated" })
      .then((data) => { setAiPreviews(data.previews || []); setAiPreviewTab(0); })
      .catch(() => {})
      .finally(() => setPreviewLoading(false));
  }, [subject, bodyHtml, personalisationMode]);

  useEffect(() => {
    if (personalisationMode !== "ai_generated") return;
    clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(fetchAiPreviews, 2000);
    return () => clearTimeout(previewDebounceRef.current);
  }, [subject, bodyHtml, personalisationMode, fetchAiPreviews]);

  const insertMergeTag = (tag) => {
    setBodyHtml((prev) => (prev || "") + tag);
  };

  const currentSample = SAMPLE_CONTACTS[sampleIndex % SAMPLE_CONTACTS.length];

  const previewHtml = useMemo(() => {
    if (!nonEmpty(bodyHtml)) return "";
    if (personalisationMode === "merge_tags") return applyMergeTagsLocally(bodyHtml, currentSample);
    return bodyHtml;
  }, [bodyHtml, personalisationMode, currentSample]);

  const normalizePickedName = (row) =>
    String(row?.name || row?.display_name || row?.recipient_name || row?.email || "").trim();

  const normalizePickedEmail = (row) =>
    String(row?.email || row?.primary_email || row?.recipient_email || "").trim().toLowerCase();

  const handleQueue = async (options = {}) => {
    const { scheduledAt, scheduleAt, tz } = options || {};
    const scheduleIso = scheduledAt || scheduleAt || null;
    const timezone    = tz || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    setIsSending(true);
    setSendError("");

    try {
      const replyToEmail = resolveReplyEmail(replyToMode, replyToCustomEmail, signature?.email);
      const finalHtml = buildEmailHTML({ subject, bodyHtml, signature, replyToEmail });

      let payload = { subject, body_html: finalHtml, tz: timezone, personalisation_mode: personalisationMode || "none" };
      if (scheduleIso) payload.schedule_at = scheduleIso;
      if (nonEmpty(afterSendStatus)) payload.afterSendStatus = afterSendStatus;

      if (audiencePlan?.deliveryMode === "filter_all") {
        payload = {
          ...payload,
          scope: "filter_all",
          expected_recipients: effectiveAudienceCount,
          // Delivery pacing: spread releases ceil(total/spread_days) per day; immediate
          // releases today's budget worth now and defers the rest (drained daily).
          pacing_mode: pacingMode === "spread" ? "spread" : "immediate",
          spread_days: pacingMode === "spread" ? (Number(pacingDays) || null) : null,
          filters: {
            ...audiencePlan.filters,
            exclude_recent: excludeRecent,
            days: Number(recentDays || audiencePlan.filters?.days || 7),
          },
        };
      } else if (picked.length > 0 && picked.every((row) => row._kind === "email_contact")) {
        payload = {
          ...payload,
          scope: "page",
          emailContactIds: picked.map((row) => Number(row._email_contact_id)).filter(Number.isFinite),
          contactIds: null,
          // Delivery pacing applies on every scope: spread releases ceil(total/spread_days)
          // per day; immediate releases today's budget worth now and defers the rest.
          pacing_mode: pacingMode === "spread" ? "spread" : "immediate",
          spread_days: pacingMode === "spread" ? (Number(pacingDays) || null) : null,
          filters: {
            q: "", limit: picked.length,
            exclude_recent: false, // user manually picked these — don't re-filter by recency
            days: 0,
            preferred_limit: preferredLimit || null,
          },
        };
      } else {
        if (!picked.length) throw new Error("No valid recipients.");
        payload = {
          ...payload,
          scope: "raw_list",
          // Delivery pacing applies on every scope (see scope: "page" above).
          pacing_mode: pacingMode === "spread" ? "spread" : "immediate",
          spread_days: pacingMode === "spread" ? (Number(pacingDays) || null) : null,
          filters: {
            exclude_recent: false, // user provided this list explicitly — don't re-filter by recency
            days: 0,
            preferred_limit: preferredLimit || null,
          },
          recipients: picked.map((row) => ({ name: normalizePickedName(row), email: normalizePickedEmail(row) })),
        };
      }

      const response = await emailCampaignsApi.create(payload);
      onQueued?.(response?.campaign_id || response?.id || null);
    } catch (err) {
      setSendError(err?.response?.data?.message || err?.message || "Failed to queue campaign.");
      throw err; // propagate so the modal can show the error in its snack bar
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => { launchHandlerRef.current = handleQueue; });
  useEffect(() => {
    if (!onLaunch) return;
    onLaunch(() => (options) => launchHandlerRef.current?.(options));
  }, [onLaunch]);

  const handleDraftWithAi = async () => {
    if (!nonEmpty(aiDraftBrief)) return;

    setAiDraftLoading(true);
    setAiDraftError("");

    try {
      const data = await emailCampaignsApi.draftWithAi({
        brief: aiDraftBrief,
        audience_name: audienceSummary?.name || audiencePlan?.name || "",
        audience_count: effectiveAudienceCount,
        reply_to_email: resolveReplyEmail(replyToMode, replyToCustomEmail, signature?.email),
      });

      if (nonEmpty(data?.subject)) {
        setSubject(data.subject);
      }

      if (nonEmpty(data?.body_html)) {
        setBodyHtml(data.body_html);
      }
    } catch (err) {
      setAiDraftError(err?.response?.data?.message || err?.message || "Failed to generate AI draft.");
    } finally {
      setAiDraftLoading(false);
    }
  };

  const showPreviewPanel = nonEmpty(bodyHtml) && personalisationMode !== "none";

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={900}>What should they receive?</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Write the message once. The campaign will use the audience you already confirmed.
      </Typography>

      <Box sx={{ mt: 2 }}>
        {sendError ? <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert> : null}

        {!signature?.imageUrl ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {companySignatureExists
              ? "You haven't set a personal email signature, so this campaign will default to sending with the company email signature."
              : "You haven't set a personal email signature, and no company signature is configured — your email will be sent without a signature image. Add one in Settings."}
          </Alert>
        ) : null}

        {/* Top toolbar row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={personalisationMode || "none"}
            onChange={(_, v) => { if (v !== null) setPersonalisationMode?.(v); }}
          >
            <Tooltip title="Same email for everyone">
              <ToggleButton value="none">None</ToggleButton>
            </Tooltip>
            <Tooltip title="Insert {{merge_tags}} that are replaced with each contact's data at send time">
              <ToggleButton value="merge_tags">Merge Tags</ToggleButton>
            </Tooltip>
            <Tooltip title="Gemini generates a unique personalised version for each contact">
              <ToggleButton value="ai_generated">
                <AutoAwesomeIcon fontSize="small" sx={{ mr: 0.5 }} />AI
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          <Button
            size="small"
            variant="outlined"
            startIcon={<BookmarkBorderIcon />}
            onClick={() => setTemplateLibraryOpen(true)}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Templates
          </Button>
        </Box>

        {/* Merge tag chip toolbar */}
        {personalisationMode === "merge_tags" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", mr: 0.5 }}>
              Insert tag:
            </Typography>
            {MERGE_TAGS.map(({ label, tag }) => (
              <Chip
                key={tag}
                label={label}
                size="small"
                variant="outlined"
                clickable
                onClick={() => insertMergeTag(tag)}
                sx={{ fontFamily: "monospace", fontSize: 11 }}
              />
            ))}
          </Box>
        )}

        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "#f9fff9",
            borderColor: "#cfe8d1",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <AutoAwesomeIcon sx={{ color: "#2e7d32" }} fontSize="small" />
            <Typography variant="subtitle2" fontWeight={800}>
              Draft with AI
            </Typography>
            <Chip
              size="small"
              label="Gemini"
              sx={{ fontWeight: 700, bgcolor: "#fff", border: "1px solid #d9e8da" }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Describe what the campaign is about and Gemini will draft the subject and the one-size-fits-all email for this audience.
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            value={aiDraftBrief}
            onChange={(e) => setAiDraftBrief(e.target.value)}
            placeholder="Example: Follow up with customers who requested flight or holiday quotes but have not replied. Remind them we can help with flights, hotels, or packages, keep it warm and helpful, and invite them to reply so we can finalise their trip."
            helperText="Best results: include the audience, offer, destination or service, important facts, and the action you want the reader to take."
          />

          {aiDraftError ? <Alert severity="error" sx={{ mt: 1.5 }}>{aiDraftError}</Alert> : null}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">
              Framework: grab attention, deliver value, clear CTA, optional P.S.
            </Typography>
            <Button
              variant="contained"
              startIcon={aiDraftLoading ? <CircularProgress color="inherit" size={16} /> : <AutoAwesomeIcon />}
              onClick={handleDraftWithAi}
              disabled={!canGenerateAiDraft}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              {aiDraftLoading ? "Generating..." : "Generate with Gemini"}
            </Button>
          </Box>
        </Paper>

        {/* Subject + Reply Buttons row */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" }, mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: { xs: "100%", md: 320 } }}>
            <TextField
              label="Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              InputProps={spamChecking ? {
                endAdornment: <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>,
              } : undefined}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", md: 340 }, ml: { xs: 0, md: "auto" } }}>
            <TextField
              select
              label="Enquiry Reply To"
              value={replyToMode}
              onChange={(e) => setReplyToMode(e.target.value)}
              fullWidth
              helperText={
                <span style={{ color: '#888', fontSize: 11 }}>
                  Select the email address subscribers will reply to when they click the button
                </span>
              }
            >
              <MenuItem value="default">Default office ({DEFAULT_REPLY_EMAIL})</MenuItem>
              <MenuItem value="my_email">My email {signature?.email ? `(${signature.email})` : ""}</MenuItem>
              <MenuItem value="other">Other email address…</MenuItem>
            </TextField>
            {replyToMode === "other" && (
              <TextField
                size="small"
                fullWidth
                placeholder="e.g. bookings@yourcompany.com"
                value={replyToCustomEmail}
                onChange={(e) => setReplyToCustomEmail(e.target.value)}
                type="email"
                sx={{ mt: 1 }}
                helperText={<span style={{ color: '#888', fontSize: 11 }}>Subscribers who click Enquire Now will email this address</span>}
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Editor + live preview side-by-side */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
          {/* Editor */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Write your email here..." />
          </Box>

          {/* Live preview panel */}
          {showPreviewPanel && (
            <Box sx={{ width: { xs: "100%", lg: 420 }, flexShrink: 0 }}>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                {/* Preview header */}
                <Box sx={{ px: 2, py: 1, bgcolor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Live Preview
                  </Typography>
                </Box>

                {personalisationMode === "merge_tags" && (
                  <>
                    {/* Contact navigator */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 0.75, bgcolor: "#f8f9ff", borderBottom: "1px solid #e0e0e0" }}>
                      <Button size="small" sx={{ minWidth: 28, p: 0.25 }} onClick={() => setSampleIndex((i) => (i - 1 + SAMPLE_CONTACTS.length) % SAMPLE_CONTACTS.length)}>
                        <ChevronLeftIcon fontSize="small" />
                      </Button>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="caption" fontWeight={700}>{currentSample.name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>{currentSample.email}</Typography>
                      </Box>
                      <Button size="small" sx={{ minWidth: 28, p: 0.25 }} onClick={() => setSampleIndex((i) => (i + 1) % SAMPLE_CONTACTS.length)}>
                        <ChevronRightIcon fontSize="small" />
                      </Button>
                    </Box>

                    <Box sx={{ height: 400, overflow: "auto", bgcolor: "#fff" }}>
                      <iframe
                        key={previewHtml}
                        srcDoc={previewHtml}
                        title="Email Preview"
                        style={{ width: "100%", minHeight: 380, border: "none" }}
                        sandbox="allow-same-origin"
                      />
                    </Box>
                  </>
                )}

                {personalisationMode === "ai_generated" && (
                  <>
                    {previewLoading && (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, py: 3 }}>
                        <CircularProgress size={18} />
                        <Typography variant="caption" color="text.secondary">Generating AI variants…</Typography>
                      </Box>
                    )}

                    {!previewLoading && aiPreviews.length === 0 && (
                      <Box sx={{ py: 3, textAlign: "center" }}>
                        <AutoAwesomeIcon sx={{ color: "text.disabled", mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          AI preview will appear after you write your subject and body.
                        </Typography>
                      </Box>
                    )}

                    {!previewLoading && aiPreviews.length > 0 && (
                      <>
                        <Tabs
                          value={aiPreviewTab}
                          onChange={(_, v) => setAiPreviewTab(v)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{ borderBottom: "1px solid #e0e0e0", minHeight: 36 }}
                        >
                          {aiPreviews.map((p, i) => (
                            <Tab
                              key={i}
                              label={p.contact_name?.split(" ")[0] || `Sample ${i + 1}`}
                              sx={{ minHeight: 36, fontSize: 12 }}
                            />
                          ))}
                        </Tabs>
                        {aiPreviews[aiPreviewTab] && (
                          <>
                            <Box sx={{ px: 2, py: 0.5, bgcolor: "#f8f9ff", borderBottom: "1px solid #e0e0e0" }}>
                              <Typography variant="caption" color="text.secondary">
                                {aiPreviews[aiPreviewTab].contact_name} · {aiPreviews[aiPreviewTab].email}
                              </Typography>
                            </Box>
                            <Box sx={{ height: 380, overflow: "auto", bgcolor: "#fff" }}>
                              <iframe
                                srcDoc={aiPreviews[aiPreviewTab].body_html}
                                title="AI Preview"
                                style={{ width: "100%", minHeight: 360, border: "none" }}
                                sandbox="allow-same-origin"
                              />
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Paper>

              {personalisationMode === "ai_generated" && (
                <Alert severity="info" sx={{ mt: 1, fontSize: 11 }}>
                  Gemini will generate a unique email for each contact before sending. This may add 1–3 minutes to dispatch time.
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {spamWarnings.length > 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {spamWarnings.map((w, i) => <div key={i}>{w}</div>)}
          </Alert>
        ) : null}

        {!hidePrimaryAction && isSending ? (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <CircularProgress size={18} />
          </Box>
        ) : null}
      </Box>

      <TemplateLibrary
        open={templateLibraryOpen}
        onClose={() => setTemplateLibraryOpen(false)}
        currentSubject={subject}
        currentBodyHtml={bodyHtml}
        onApply={({ subject: s, body_html: b }) => {
          if (s) setSubject(s);
          if (b) setBodyHtml(b);
        }}
      />
    </Paper>
  );
}
